import React, { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { ViewStyle } from 'react-native'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'

import styled from 'styled-components/native'

import {
  BEND_MANAGED_CALENDAR_NAME,
  CALENDAR_SETUP_LEARN_MORE_LINK_DA,
  CALENDAR_SETUP_LEARN_MORE_LINK_OUS,
} from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/constants'
import {
  CALENDAR_CONFIGURATION_ADMIN_STATUS,
  CALENDAR_PROVIDER_ADMIN_STATUS,
  PROVIDER_CALENDAR_USAGE,
} from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { ProviderCalendarAuthorizationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/providerCalendarAuthorizationModal/ProviderCalendarAuthorizationModal'
import { ProviderCalendarSetupCard } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/providerCalendarSetupCard/ProviderCalendarSetupCard'
import { ProviderHybridCalendarAuthorizationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/providerHybridCalendarAuthorizationModal/ProviderHybridCalendarAuthorizationModal'

import { OAUTH_SUCCESS_RESPONSE } from './constants'
import {
  useLazyGetAvailableCalendarsQuery,
  useLazyGetOAuthURLQuery,
  usePatchCalendarProviderMutation,
  usePutAvailableCalendarsMutation,
} from './data/calendarApi'
import { useCalendarRequiresAuth } from './data/hooks/useCalendarRequiresAuth'
import { useSetupRoute } from './hooks/useSetupRoute'
import { useTimeZone } from './hooks/useTimeZone'
import { actions as mixpanelActions, mixpanelEvents } from '../../../mixpanel/mixpanelConstants'
import { track } from '../../../mixpanel/mixpanelTracking'
import {
  getAuthConfig,
  getAuthImpersonatedProviderId,
  getAuthUserEmail,
  getAuthUserId,
} from '../data/auth/authSelectors'

const OuterContainer = styled.View({
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
})

const SetupContainer = styled.View({
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  maxWidth: '944px',
})

type CalendarSetupCardProps = {
  style?: ViewStyle
}

const CalendarSetupCard: FunctionComponent<CalendarSetupCardProps> = ({ style }) => {
  const userId = useSelector(getAuthUserId)
  const config = useSelector(getAuthConfig)
  const userEmail = useSelector(getAuthUserEmail)
  const impersonatedProviderId = useSelector(getAuthImpersonatedProviderId)
  const timeZone = useTimeZone()
  const requiresAuth = useCalendarRequiresAuth()
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const navigate = useNavigate()
  const [getOAuthURL] = useLazyGetOAuthURLQuery()
  const [patchCalendarProvider] = usePatchCalendarProviderMutation()
  const [getAvailableCalendars] = useLazyGetAvailableCalendarsQuery()
  const [putAvailableCalendars] = usePutAvailableCalendarsMutation()

  const isImpersonating = !!impersonatedProviderId
  const isSimplifiedFlow = config?.inProductCalendar?.simplifiedSetupFlow
  const bookingMode = config?.inProductCalendar?.bookingMode
  const dailyStart = config?.inProductCalendar?.dailyStart
  const dailyEnd = config?.inProductCalendar?.dailyEnd

  const setupRoute = useSetupRoute(config)

  const startOAuthFlow = useCallback(() => {
    if (!userEmail || !userId) {
      return
    }
    getOAuthURL({
      providerId: userId,
      providerEmail: userEmail,
      timeZone,
    })
      .unwrap()
      .then(({ authorization_uri: url }) => {
        window.open(url)
      })
  }, [getOAuthURL, timeZone, userEmail, userId])

  const configureBendCalendarsAfterOAuth = useCallback(async () => {
    if (!userId || !bookingMode) {
      return
    }
    await patchCalendarProvider({
      providerId: userId,
      body: {
        booking_mode: bookingMode,
        admin_status: CALENDAR_PROVIDER_ADMIN_STATUS.ACTIVE,
        ...(dailyStart && { daily_start: dailyStart }),
        ...(dailyEnd && { daily_end: dailyEnd }),
      },
    }).unwrap()

    const calendars = await getAvailableCalendars({ providerId: userId }).unwrap()
    const configurations = calendars
      .filter((calendar) => calendar.calendar_name === BEND_MANAGED_CALENDAR_NAME || calendar.is_primary)
      .map((calendar) => ({
        ...calendar,
        admin_status: CALENDAR_CONFIGURATION_ADMIN_STATUS.PUBLISHED,
        calendar_usage:
          calendar.calendar_name === BEND_MANAGED_CALENDAR_NAME
            ? PROVIDER_CALENDAR_USAGE.WRITE_AVAILABILITY
            : PROVIDER_CALENDAR_USAGE.WRITE_APPOINTMENTS,
      }))

    if (configurations.length > 0) {
      await putAvailableCalendars({ provider_id: userId, configurations }).unwrap()
    }

    if (configurations.length < 2) {
      console.warn(`Expected 2 calendar configurations (Availability + primary), but found ${configurations.length}`)
    }
  }, [userId, bookingMode, dailyStart, dailyEnd, patchCalendarProvider, getAvailableCalendars, putAvailableCalendars])

  const location = useLocation()

  useEffect(() => {
    const onMessage = async (event: MessageEvent<any>) => {
      if (event.origin !== window.location.origin) {
        return
      }
      if (typeof event.data !== 'string') {
        return
      }
      if (event.data === OAUTH_SUCCESS_RESPONSE) {
        try {
          await configureBendCalendarsAfterOAuth()
        } catch (error) {
          console.error('Failed to configure calendar:', error)
        }
        // When isBendProviderUsingCSForAvailability flag is on, useSetupRoute returns /lc-calendar/current,
        // which should be the same route we're on. In that case, refresh to update auth state.
        if (location.pathname === setupRoute) {
          navigate(0)
        } else {
          navigate(setupRoute)
        }
      }
    }
    window.addEventListener('message', onMessage)
    return () => {
      window.removeEventListener('message', onMessage)
    }
  }, [navigate, setupRoute, configureBendCalendarsAfterOAuth, location.pathname])

  return (
    <OuterContainer style={style}>
      <SetupContainer>
        <ProviderCalendarSetupCard
          //TODO: May need unique mixpanel event for DA: DACN-1336
          onLearnMorePressed={() => {
            track({ event: mixpanelEvents.BUTTON_PRESS, action: mixpanelActions.CALENDAR_SETUP_LEARN_MORE })
            window.open(
              config?.inProductCalendar?.useInternationalLinks
                ? CALENDAR_SETUP_LEARN_MORE_LINK_OUS
                : config?.inProductCalendar?.hybridBookingMode
                  ? CALENDAR_SETUP_LEARN_MORE_LINK_DA
                  : (config?.inProductCalendar?.setupCalendarZendeskArticle ??
                    'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/35842185897235--PRODUCT-UPDATE-Calendar-Schedule-Feature-How-to-Set-up-your-Lyra-Calendar'),
              '_blank',
            )
          }}
          onSetupPressed={() => {
            if (isImpersonating) {
              return
            }
            track({ event: mixpanelEvents.BUTTON_PRESS, action: mixpanelActions.CALENDAR_SETUP_SET_UP_CALENDAR })
            if (isSimplifiedFlow || requiresAuth) {
              setIsAuthModalOpen(true)
            } else {
              navigate(setupRoute)
            }
          }}
          hybridBookingMode={config?.inProductCalendar?.hybridBookingMode}
          isImpersonating={isImpersonating}
        />
        {config?.inProductCalendar?.hybridBookingMode ? (
          <ProviderHybridCalendarAuthorizationModal
            onAuthorizePressed={startOAuthFlow}
            onCancelPressed={() => setIsAuthModalOpen(false)}
            isVisible={isAuthModalOpen}
            useInternationalLinks={config?.inProductCalendar?.useInternationalLinks}
          />
        ) : (
          <ProviderCalendarAuthorizationModal
            onAuthorizePressed={startOAuthFlow}
            onCancelPressed={() => setIsAuthModalOpen(false)}
            isVisible={isAuthModalOpen}
          />
        )}
      </SetupContainer>
    </OuterContainer>
  )
}

export default CalendarSetupCard
